import { ODataEntitySetService, ODataServiceFactory } from 'angular-odata';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FilterModel, IServerSideGetRowsParams, SortModelItem } from 'ag-grid-community';
import {
  convertToFilterString,
  convertToOrderByString,
  CustomFilterRule,
  CustomSortRule,
  SetFilterRule,
} from '@workspace-app/shared';
import { CoaterLicense } from '@workspace-app/odatabackend/QualicoatBackendModel';

@Injectable({
  providedIn: 'root',
})
export class LicenseDataService {
  private factory = inject(ODataServiceFactory);
  private httpClient = inject(HttpClient);

  licenseNumberService: ODataEntitySetService<CoaterLicense>;

  constructor() {
    this.licenseNumberService = this.factory.entitySet<CoaterLicense>(
      'Licenses',
      'QualicoatBackend.Features.Certification.Licenses.QualicoatLicense'
    );
  }

  public getRows(params: IServerSideGetRowsParams) {
    const endRow = params.request.endRow || 20;
    const startRow = params.request.startRow || 0;
    // Create a fresh entities resource for each query to avoid accumulating state
    const freshEntities = this.licenseNumberService.entities();
    return freshEntities
      ?.query((q) => {
        q.expand('ProductionSite($expand=Customer)');
        q.top(endRow);
        q.skip(startRow);
        if (params.request.sortModel?.length > 0) q.orderBy(this.convertToOrderByString(params.request.sortModel));
        if (params.request.filterModel) q.filter(this.convertToFilterString(params.request.filterModel));
      })
      .fetch({ withCount: true })
      .subscribe((response) => {
        const count = response.annots['annotations'].get('@odata.count');
        if (response.entities) {
          return params.success({ rowData: response.entities, rowCount: count });
        }
        return params.fail();
      });
  }

  withdrawLicense(licenseNumberId: string, withdrawalDate: Date, withdrawalReason: string) {
    return this.licenseNumberService.update(licenseNumberId, {
      withdrawalDate: withdrawalDate.toUTCString(),
      withdrawalReason,
    });
  }

  grantLicense(licenseNumberId: string, expiryDate: Date) {
    return this.licenseNumberService.update(licenseNumberId, { expiryDate: expiryDate.toUTCString() });
  }

  archive(licenseId: string) {
    return this.httpClient.post(`/api/licenses/${licenseId}/archive`, {});
  }

  unarchive(licenseId: string) {
    return this.httpClient.post(`/api/licenses/${licenseId}/unarchive`, {});
  }

  /**
   * Fetch a single license by ID with related entities
   */
  fetchLicense(licenseId: string): Observable<CoaterLicense> {
    return this.licenseNumberService
      .entity(licenseId)
      .query((q) => q.expand('ProductionSite($expand=Customer)'))
      .fetch()
      .pipe(map((response) => response.entity!));
  }

  private convertToOrderByString(sortModel: SortModelItem[]) {
    const customSortRules: CustomSortRule[] = [
      {
        key: 'completeLicenseNumber',
        fn: (key: string, sortOrder: 'asc' | 'desc') => {
          // Sort logic for license numbers:
          // 1. First by isGranting (granting licenses like "GR123" come first/last)
          // 2. Then by grantingNumber for granting licenses
          // 3. Then by prefix for regular licenses
          // 4. Finally by number
          return `isGranting ${sortOrder}, grantingNumber ${sortOrder}, prefix ${sortOrder}, number ${sortOrder}`;
        },
      },
    ];
    return convertToOrderByString({ sortModel, customSortRules });
  }

  private convertToFilterString(filterModel: FilterModel) {
    const customFilterRules: CustomFilterRule[] = [
      {
        key: 'completeLicenseNumber',
        fn: (key: string, filterType: string, type: string, filter: string) => {
          let prefix: string | null = null;
          let number: string | null = null;
          if (filter.includes('-')) {
            prefix = filter.split('-')[0];
            number = filter.split('-')[1];
          } else {
            if (/^\d+$/.test(filter)) {
              number = filter;
            } else {
              prefix = filter;
            }
          }
          const subFilters: string[] = [];
          if (type == 'contains') {
            if (prefix) subFilters.push(`(contains(tolower(prefix), tolower('${prefix}')))`);
            if (number) subFilters.push(`(contains(cast(Number, 'Edm.String'), '${number}'))`);
          } else if (type == 'equals') {
            if (prefix) subFilters.push(`(tolower(prefix) eq tolower('${prefix}'))`);
            if (number) subFilters.push(`(Number eq ${number})`);
          }
          return `(${subFilters.join(' and ')})`;
        },
      },
    ];
    const setFilterRules: SetFilterRule[] = [
      {
        key: 'seaside',
        fn: (key: string, values: string[]) => {
          const subFilters: string[] = [];
          values.forEach((value) => {
            subFilters.push(`${key} eq ${value}`);
          });
          return `(${subFilters.join(' or ')})`;
        },
      },
      {
        key: 'class',
        fn: (key: string, values: string[]) => {
          const subFilters: string[] = [];
          values.forEach((value) => {
            subFilters.push(`${key} eq ${value}`);
          });
          return `(${subFilters.join(' or ')})`;
        },
      },
      {
        key: 'isValid',
        fn: (key: string, values: string[]) => {
          const subFilters: string[] = [];
          values.forEach((value) => {
            if (typeof value === 'boolean') {
              subFilters.push(this.isValidCondition(value));
            }
          });
          return `(${subFilters.join(' or ')})`;
        },
      },
      {
        key: 'licenseType',
        fn: (key: string, values: string[]) => {
          const subFilters: string[] = [];
          values.forEach((value) => {
            subFilters.push(`LicenseType eq '${value}'`);
          });
          return `(${subFilters.join(' or ')})`;
        },
      },
    ];
    return convertToFilterString({ filterModel, setFilterRules, customFilterRules });
  }

  private isValidCondition(shouldBeValid: boolean): string {
    const todayString = new Date().toISOString().split('T')[0];
    if (shouldBeValid) {
      return `expiryDate gt ${todayString} and (withdrawalDate eq null or withdrawalDate gt ${todayString})`;
    } else {
      return `expiryDate lt ${todayString} or (withdrawalDate ne null and withdrawalDate lt ${todayString})`;
    }
  }
}
